import { useEffect, useState } from "react";
import { Bell, BellRing, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { enablePush, isPushEnabled, pushSupport } from "@/lib/push";
import { toast } from "sonner";

/** Banner para activar avisos push de cupones (campañas + vencimientos). */
export default function PushOptIn({ deviceId, dark = true, compact = false }) {
  const [state, setState] = useState("loading"); // loading | ok | enabled | denied | ios_needs_install | unsupported | hidden
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!deviceId) return;
    if (localStorage.getItem("hr_push_dismissed") === "1") return setState("hidden");
    (async () => {
      const s = pushSupport();
      if (s !== "ok") return setState(s);
      setState((await isPushEnabled()) ? "enabled" : "ok");
    })();
  }, [deviceId]);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush(deviceId);
      setState("enabled");
      toast.success("¡Listo! Te avisaremos cuando llegue un cupón o esté por vencer");
    } catch (e) {
      if (e.message === "denied") { setState("denied"); toast.error("Bloqueaste las notificaciones en tu navegador"); }
      else toast.error("No pude activar los avisos en este navegador");
    } finally { setBusy(false); }
  };

  const dismiss = () => { localStorage.setItem("hr_push_dismissed", "1"); setState("hidden"); };

  if (["loading", "hidden", "unsupported", "denied"].includes(state)) return null;

  if (state === "enabled") {
    return (
      <div className={`flex items-center gap-2 text-xs font-bold ${dark ? "text-emerald-300" : "text-emerald-700"}`} data-testid="push-enabled">
        <BellRing className="w-4 h-4" /> Avisos activados — te notificamos cupones nuevos y por vencer
      </div>
    );
  }

  const base = dark ? "bg-white/5 border-white/10 text-white" : "bg-white border-neutral-200 text-black";
  return (
    <div className={`rounded-2xl border ${base} ${compact ? "p-3" : "p-4"} flex items-start gap-3`} data-testid="push-optin">
      <div className="w-10 h-10 rounded-xl bg-[#FF007A]/15 text-[#FF007A] grid place-items-center shrink-0"><Bell className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-extrabold text-sm">No te pierdas ningún cupón</div>
        {state === "ios_needs_install" ? (
          <p className={`text-xs mt-0.5 ${dark ? "text-white/60" : "text-neutral-600"}`}>
            En iPhone: toca <Share className="inline w-3 h-3 -mt-0.5" /> Compartir → “Añadir a pantalla de inicio” y abre la app desde ahí para activar avisos.
          </p>
        ) : (
          <>
            <p className={`text-xs mt-0.5 ${dark ? "text-white/60" : "text-neutral-600"}`}>Recibe un aviso cuando un comercio te envíe un cupón o cuando uno esté por vencer.</p>
            <Button onClick={enable} disabled={busy} size="sm" className="mt-2 rounded-full bg-[#FF007A] hover:bg-[#e6006e] h-9 font-bold text-xs" data-testid="push-enable-btn">
              <BellRing className="w-3.5 h-3.5" /> {busy ? "Activando…" : "Activar avisos"}
            </Button>
          </>
        )}
      </div>
      <button onClick={dismiss} className={`${dark ? "text-white/40 hover:text-white" : "text-neutral-400 hover:text-black"}`} aria-label="Cerrar" data-testid="push-dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}
